import { useCallback, useEffect, useRef, useState } from 'react';
import * as _ from 'underscore';

import * as graphql from 'generated/graphql';

import useImperativeSearch from './useImperativeSearch';

export default function useSearch(
	query: string,
	options?: {
		filters?: string;
		limit?: number;
		skip?: boolean;
		excludeViewer?: boolean;
		status?: graphql.UserStatus | graphql.UserStatus[];
	},
) {
	const lock = useRef(false);
	const dirty = useRef(false);
	const ref = useRef({ query, options });
	const [loading, setLoading] = useState(!options?.skip);
	const [rawLoading, setRawLoading] = useState(!options?.skip);
	const [results, setResults] = useState<any[]>([]);
	const [version, setVersion] = useState(0);
	const search = useImperativeSearch({ excludeViewer: options?.excludeViewer });

	const reload = useCallback(
		_.debounce(async () => {
			if (lock.current) {
				dirty.current = true;

				return;
			}

			dirty.current = false;
			lock.current = true;

			setResults(await search(ref.current.query, ref.current.options));
			setVersion((version) => version + 1);

			lock.current = false;

			if (dirty.current) {
				reload();
			} else {
				setRawLoading(false);
				setLoading(false);
			}
		}, 100),
		[],
	);

	useEffect(() => {
		if (options?.skip) {
			return;
		}

		ref.current.query = query;
		ref.current.options = options;

		setRawLoading(true);

		// Wait 200ms before showing the spinner.
		setTimeout(() => {
			if (dirty.current && lock.current) {
				setLoading(true);
			}
		}, 200);

		reload();
	}, [query, options?.filters, options?.skip, JSON.stringify(options?.status)]);

	return { loading, results, version, reload, rawLoading };
}
